import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';

// Types
export type FacialVerificationOperationType = 
  | 'PIX_SEND' 
  | 'TRANSFER' 
  | 'WITHDRAWAL' 
  | 'TOKEN_PURCHASE' 
  | 'CONTRACT_SIGN';

export interface FacialVerificationRule {
  id: string;
  tenantId: string | null;
  name: string;
  description: string | null;
  operationType: FacialVerificationOperationType;
  valueThreshold: string; // BigInt as string
  timeWindowStart: string | null; // HH:mm format
  timeWindowEnd: string | null;
  timezone: string;
  conditionType: 'AND' | 'OR';
  isEnabled: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  tenant?: {
    id: string;
    name: string;
  };
}

export interface CreateFacialVerificationRuleDto {
  name: string;
  description?: string;
  operationType: FacialVerificationOperationType;
  valueThreshold: string;
  timeWindowStart?: string;
  timeWindowEnd?: string;
  timezone?: string;
  conditionType?: 'AND' | 'OR';
  priority?: number;
  tenantId?: string;
}

export interface UpdateFacialVerificationRuleDto {
  name?: string;
  description?: string;
  valueThreshold?: string;
  timeWindowStart?: string | null;
  timeWindowEnd?: string | null;
  timezone?: string;
  conditionType?: 'AND' | 'OR';
  priority?: number;
  isEnabled?: boolean;
}

export interface RuleFilters {
  tenantId?: string;
  operationType?: FacialVerificationOperationType;
  isEnabled?: boolean;
  includeGlobal?: boolean;
}

export interface FacialVerificationAuditLog {
  id: string;
  tenantId: string;
  userId: string;
  operationType: FacialVerificationOperationType;
  operationValue: string;
  ruleId: string | null;
  verificationRequired: boolean;
  verificationResult: string | null;
  sessionId: string | null;
  bypassTokenUsed: boolean;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  tenant?: {
    id: string;
    name: string;
  };
  user?: {
    id: string;
    name: string;
    email: string;
  };
  rule?: {
    id: string;
    name: string;
  };
}

export interface AuditLogFilters {
  tenantId?: string;
  userId?: string;
  operationType?: FacialVerificationOperationType;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface GenerateBypassTokenDto {
  userId: string;
  operationType: FacialVerificationOperationType;
  expiresInMinutes?: number;
}

export interface BypassTokenResponse {
  token: string;
  expiresInMinutes: number;
  operationType: FacialVerificationOperationType;
  userId: string;
}

// ==================== RULES HOOKS ====================

export function useFacialVerificationRules(filters?: RuleFilters) {
  return useQuery<FacialVerificationRule[]>({
    queryKey: ['facial-verification-rules', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.tenantId) params.append('tenantId', filters.tenantId);
      if (filters?.operationType) params.append('operationType', filters.operationType);
      if (filters?.isEnabled !== undefined) params.append('isEnabled', String(filters.isEnabled));
      if (filters?.includeGlobal !== undefined) params.append('includeGlobal', String(filters.includeGlobal));
      
      const url = `${ENDPOINTS.facialVerification.rules}${params.toString() ? `?${params}` : ''}`;
      const response = await api.get(url);
      return response.data;
    },
  });
}

export function useFacialVerificationRule(id: string) {
  return useQuery<FacialVerificationRule>({
    queryKey: ['facial-verification-rule', id],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.facialVerification.ruleById(id));
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateFacialVerificationRule() {
  const queryClient = useQueryClient();
  
  return useMutation<FacialVerificationRule, Error, CreateFacialVerificationRuleDto>({
    mutationFn: async (data) => {
      const response = await api.post(ENDPOINTS.facialVerification.rules, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facial-verification-rules'] });
    },
  });
}

export function useUpdateFacialVerificationRule() {
  const queryClient = useQueryClient();
  
  return useMutation<FacialVerificationRule, Error, { id: string; data: UpdateFacialVerificationRuleDto }>({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(ENDPOINTS.facialVerification.ruleById(id), data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['facial-verification-rules'] });
      queryClient.invalidateQueries({ queryKey: ['facial-verification-rule', id] });
    },
  });
}

export function useDeleteFacialVerificationRule() {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await api.delete(ENDPOINTS.facialVerification.ruleById(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facial-verification-rules'] });
    },
  });
}

export function useEnableFacialVerificationRule() {
  const queryClient = useQueryClient();
  
  return useMutation<FacialVerificationRule, Error, string>({
    mutationFn: async (id) => {
      const response = await api.post(ENDPOINTS.facialVerification.enableRule(id));
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['facial-verification-rules'] });
      queryClient.invalidateQueries({ queryKey: ['facial-verification-rule', id] });
    },
  });
}

export function useDisableFacialVerificationRule() {
  const queryClient = useQueryClient();
  
  return useMutation<FacialVerificationRule, Error, string>({
    mutationFn: async (id) => {
      const response = await api.post(ENDPOINTS.facialVerification.disableRule(id));
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['facial-verification-rules'] });
      queryClient.invalidateQueries({ queryKey: ['facial-verification-rule', id] });
    },
  });
}

// ==================== AUDIT LOGS HOOKS ====================

export function useFacialVerificationAuditLogs(filters?: AuditLogFilters) {
  return useQuery<{ logs: FacialVerificationAuditLog[]; total: number; page: number; limit: number }>({
    queryKey: ['facial-verification-audit-logs', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.tenantId) params.append('tenantId', filters.tenantId);
      if (filters?.userId) params.append('userId', filters.userId);
      if (filters?.operationType) params.append('operationType', filters.operationType);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));
      
      const url = `${ENDPOINTS.facialVerification.auditLogs}${params.toString() ? `?${params}` : ''}`;
      const response = await api.get(url);
      return response.data;
    },
  });
}

// ==================== BYPASS TOKEN HOOKS ====================

export function useGenerateBypassToken() {
  return useMutation<BypassTokenResponse, Error, GenerateBypassTokenDto>({
    mutationFn: async (data) => {
      const response = await api.post(ENDPOINTS.facialVerification.bypassTokens, data);
      return response.data;
    },
  });
}

// ==================== CONSTANTS ====================

export const OPERATION_TYPE_LABELS: Record<FacialVerificationOperationType, string> = {
  PIX_SEND: 'Envio de PIX',
  TRANSFER: 'Transferência',
  WITHDRAWAL: 'Saque',
  TOKEN_PURCHASE: 'Compra de Token',
  CONTRACT_SIGN: 'Assinatura de Contrato',
};

export const OPERATION_TYPES: { value: FacialVerificationOperationType; label: string }[] = [
  { value: 'PIX_SEND', label: 'Envio de PIX' },
  { value: 'TRANSFER', label: 'Transferência' },
  { value: 'WITHDRAWAL', label: 'Saque' },
  { value: 'TOKEN_PURCHASE', label: 'Compra de Token' },
  { value: 'CONTRACT_SIGN', label: 'Assinatura de Contrato' },
];

export const CONDITION_TYPES = [
  { value: 'OR', label: 'OU (qualquer condição)' },
  { value: 'AND', label: 'E (todas as condições)' },
];


// ==================== VERIFICATION FLOW HOOK ====================

export interface VerificationRequiredResponse {
  requiresVerification: boolean;
  sessionId?: string;
  reason?: string;
  triggeredRuleId?: string;
  triggeredRuleName?: string;
}

export interface UseVerificationFlowOptions {
  onVerificationRequired?: (response: VerificationRequiredResponse) => void;
  onVerificationComplete?: (sessionId: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook para gerenciar o fluxo de verificação facial em operações
 * 
 * Uso:
 * ```tsx
 * const { checkAndExecute, isVerifying, verificationData, clearVerification } = useFacialVerificationFlow({
 *   onVerificationComplete: (sessionId) => {
 *     // Re-executar operação com sessionId
 *   }
 * });
 * 
 * // Na operação
 * const result = await checkAndExecute(async () => {
 *   return await api.post('/banking/pix/send', data);
 * });
 * 
 * if (result.requiresVerification) {
 *   // Mostrar modal de verificação
 * }
 * ```
 */
export function useFacialVerificationFlow(options?: UseVerificationFlowOptions) {
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationData, setVerificationData] = useState<VerificationRequiredResponse | null>(null);

  const checkAndExecute = async <T>(
    operation: () => Promise<{ data: T & Partial<VerificationRequiredResponse> }>
  ): Promise<{ success: boolean; data?: T; requiresVerification?: boolean; verificationData?: VerificationRequiredResponse }> => {
    setIsVerifying(true);
    
    try {
      const response = await operation();
      const data = response.data;
      
      // Verificar se a resposta indica necessidade de verificação
      if (data.requiresVerification) {
        const verificationResponse: VerificationRequiredResponse = {
          requiresVerification: true,
          sessionId: data.sessionId,
          reason: data.reason,
          triggeredRuleId: data.triggeredRuleId,
          triggeredRuleName: data.triggeredRuleName,
        };
        
        setVerificationData(verificationResponse);
        options?.onVerificationRequired?.(verificationResponse);
        
        return {
          success: false,
          requiresVerification: true,
          verificationData: verificationResponse,
        };
      }
      
      // Operação bem-sucedida sem necessidade de verificação
      return {
        success: true,
        data: data as T,
      };
    } catch (error) {
      options?.onError?.(error as Error);
      throw error;
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerificationComplete = (sessionId: string) => {
    setVerificationData(null);
    options?.onVerificationComplete?.(sessionId);
  };

  const clearVerification = () => {
    setVerificationData(null);
  };

  return {
    checkAndExecute,
    isVerifying,
    verificationData,
    handleVerificationComplete,
    clearVerification,
  };
}
